import { z } from 'zod';

import type { Api, ApiFilterBuilder } from '@automapa/svelte-automapa-template';
import { ApiInterface } from '../../../utilities/ApiInterface';
import {
    CalculationResponseSchema,
    CalculationResultResponseSchema,
    ConnectionResponseSchema,
    JobCountSchema,
    type JobCountType,
} from '../types';
import { LocationResponseSchema } from '../../Location/types';
import { type QueriesType, QueriesTypeSchema } from '../../telematics/types';
import { RouteTypeResponseSchema } from '../../RouteType/RouteTypeApi';

export const RouteResponseSchema = z.object({
    unique_id: z.string(),
    connection: ConnectionResponseSchema,
    location_from: LocationResponseSchema,
    location_to: LocationResponseSchema,
    calculation: CalculationResponseSchema,
    calculation_result: CalculationResultResponseSchema,
    modified: z.boolean(),
    route_type: RouteTypeResponseSchema.nullable().catch(null),
    outdated: z.boolean(),
});
export type RouteResponseType = z.infer<typeof RouteResponseSchema>;

export class RoutesApi extends ApiInterface {
    private static endpoint: string = 'view_connection';

    public static async getAll(api: Api, filter: ApiFilterBuilder): Promise<RouteResponseType[]> {
        return super._getAll<RouteResponseType>(api, filter, this.endpoint, RouteResponseSchema);
    }

    public static async getOne(api: Api, id: number): Promise<RouteResponseType> {
        return super._getOne<RouteResponseType>(api, id, this.endpoint, RouteResponseSchema);
    }

    public static async query__DEPRECATED(
        api: Api,
        filter: ApiFilterBuilder,
        queries: QueriesType
    ): Promise<RouteResponseType[]> {
        return super._query<RouteResponseType>(
            api,
            queries,
            filter,
            `${this.endpoint}/get_routes`,
            RouteResponseSchema
        );
    }

    public static async query(
        api: Api,
        queries: QueriesType,
        filter: ApiFilterBuilder
    ): Promise<RouteResponseType[]> {
        let uri = filter.getUri();
        return api.getData<QueriesType, RouteResponseType[]>(
            'post',
            `/${this.endpoint}/query${uri}`,
            queries,
            z.any(),
            z.array(RouteResponseSchema)
        );
    }

    public static async getCountWithQueries(
        api: Api,
        queries: QueriesType,
        filter: ApiFilterBuilder
    ): Promise<JobCountType> {
        return api.getData<QueriesType, JobCountType>(
            'post',
            `/${this.endpoint}/count/query` + filter.getUri(),
            queries,
            QueriesTypeSchema,
            JobCountSchema
        );
    }

    public static async getCurrentTransactionCount(api: Api): Promise<JobCountType> {
        return api.getData<never, JobCountType>(
            'get',
            `/route/current_transaction_count`,
            undefined,
            z.void(),
            JobCountSchema
        );
    }
}
